import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, Check, GraduationCap, ArrowUpRight, Youtube, Sparkles, Cookie } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ChiSono from "@/sections/ChiSono";
import { CATALOG_ITEMS, CORSO_INCLUDE, YOUTUBE, IMAGES, LINKS } from "@/data/content";

const FILTERS = [
  { id: "tutti", label: "Tutti" },
  { id: "gratuiti", label: "Gratuiti" },
  { id: "premium", label: "Premium" },
];

const Corso = () => {
  const [filter, setFilter] = useState("tutti");
  const items = CATALOG_ITEMS.filter(
    (it) => filter === "tutti" || (filter === "gratuiti" ? it.free : !it.free)
  );

  return (
    <div data-testid="section-corso" className="mx-auto max-w-md px-5 pb-10 pt-14 sm:max-w-2xl sm:pt-20 lg:max-w-5xl lg:px-8">
      <ChiSono />
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-cacao p-8 sm:p-12">
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "radial-gradient(ellipse 70% 80% at 80% 0%, rgba(217,119,6,0.35), transparent 65%)" }}
          />
          <div className="relative grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-oro-light">
                <GraduationCap className="h-4 w-4" /> Torte Perfette Senza Stress
              </span>
              <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight text-crema sm:text-4xl">
                Da appassionata a Cake Designer
              </h1>
              <p className="mt-4 max-w-md text-sm text-crema/70 leading-relaxed">
                Il percorso completo su Hotmart: dalle basi della pasta di
                zucchero ai personaggi 3D scenografici, in 5 moduli da vedere
                con calma, con schede tecniche da scaricare.
              </p>
              <a
                data-testid="hotmart-cta-corso-main"
                href={LINKS.hotmart}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-hotmart px-8 py-3.5 text-sm font-bold text-white shadow-[0_12px_35px_rgba(230,81,0,0.45)] transition-colors hover:bg-hotmart-dark"
              >
                Iscriviti su Hotmart
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <div className="mt-8 flex items-center gap-4">
                <img
                  src={IMAGES.insegnante}
                  alt="L'insegnante del corso con la sua torta decorata con personaggi in pasta di zucchero"
                  data-testid="corso-teacher-photo"
                  className="h-16 w-16 rounded-full border-2 border-oro-light/60 object-cover object-top shadow-lg"
                />
                <p className="font-hand text-2xl text-oro-light">
                  la tua insegnante ti aspetta!
                </p>
              </div>
            </div>
            <ul className="space-y-3 self-center">
              {CORSO_INCLUDE.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-crema/85" data-testid={`corso-include-${i + 1}`}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rosa/25">
                    <Check className="h-3 w-3 text-blush" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <div className="mt-16">
        <Reveal>
          <Eyebrow>Tutorial & Personaggi</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-cacao sm:text-3xl">
            Il catalogo delle creazioni
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-6 flex gap-2" data-testid="catalog-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`catalog-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
                  filter === f.id
                    ? "bg-cacao text-crema"
                    : "border border-pink-200 bg-white text-cacao-muted hover:border-rosa"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
        <motion.div layout className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {items.map((item) => (
              <motion.article
                layout
                key={item.id}
                data-testid={`catalog-card-${item.id}`}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="group overflow-hidden rounded-3xl border border-pink-100 bg-white transition-shadow hover:shadow-[0_18px_45px_rgba(45,36,36,0.12)]"
              >
                <div className="relative overflow-hidden">
                  {item.image ? (
                    <img
                      src={IMAGES[item.image]}
                      alt={`${item.title} — ${item.cat} in pasta di zucchero`}
                      className={`aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105 ${item.pos || ""} ${!item.free ? "saturate-[0.9]" : ""}`}
                    />
                  ) : (
                    <div
                      className={`flex aspect-square w-full flex-col items-center justify-center gap-2 p-4 text-center ${
                        item.teaser
                          ? "bg-cacao"
                          : "bg-gradient-to-br from-blush via-burro to-menta"
                      }`}
                    >
                      {item.teaser ? (
                        <Sparkles className="h-8 w-8 text-oro" />
                      ) : (
                        <Cookie className="h-8 w-8 text-rosa" />
                      )}
                      <p className={`font-hand text-3xl leading-tight ${item.teaser ? "text-oro-light" : "text-rosa-dark"}`}>
                        {item.teaser ? "in arrivo..." : item.title}
                      </p>
                    </div>
                  )}
                  <span
                    data-testid={`badge-${item.teaser ? "soon" : item.free ? "free" : "premium"}-${item.id}`}
                    className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                      item.teaser
                        ? "bg-red-100 text-red-800"
                        : item.free
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-900"
                    }`}
                  >
                    {item.teaser ? (
                      <><Sparkles className="h-3 w-3" /> In Arrivo</>
                    ) : item.free ? (
                      <><Unlock className="h-3 w-3" /> Gratuito</>
                    ) : (
                      <><Lock className="h-3 w-3" /> Premium</>
                    )}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-cacao-light">{item.cat}</p>
                  <h3 className="mt-1 font-display text-base font-bold leading-snug text-cacao">{item.title}</h3>
                  {item.teaser ? (
                    <p data-testid={`teaser-${item.id}`} className="mt-2 text-xs font-semibold leading-relaxed text-orange-600">
                      {item.teaserText}
                    </p>
                  ) : item.soon ? (
                    <p
                      data-testid={`soon-${item.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-oro-light px-3 py-1 text-[11px] font-bold text-oro"
                    >
                      Presto in arrivo gratuitamente
                    </p>
                  ) : item.free ? (
                    item.tutorial ? (
                      <a
                        data-testid={`tutorial-link-${item.id}`}
                        href={item.tutorial || YOUTUBE.watch}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-menta-dark transition-colors hover:text-rosa-dark"
                      >
                        <Youtube className="h-3.5 w-3.5" /> Guarda il tutorial
                      </a>
                    ) : (
                      <p
                        data-testid={`tag-${item.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-menta px-3 py-1 text-[11px] font-bold text-menta-dark"
                      >
                        {item.tag}
                      </p>
                    )
                  ) : (
                    <a
                      data-testid={`premium-unlock-${item.id}`}
                      href={item.link || LINKS.hotmart}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-hotmart transition-colors hover:text-hotmart-dark"
                    >
                      Sblocca la guida <ArrowUpRight className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Corso;
